/**
 * useMfa - MFA setup (initiate, verify), verify code, disable, regenerate backup codes.
 * MODULE: core/auth/frontend/hooks/useMfa.ts
 */
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;
const getToken = () => localStorage.getItem('access_token');

async function request(
  path: string,
  options: { method?: string; body?: unknown } = {}
) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/mfa${path}`, {
    method: options.method || 'GET',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
    ...(options.body !== undefined && options.body !== null
      ? { body: JSON.stringify(options.body) }
      : {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || err.message || 'MFA request failed');
  }
  return res.json();
}

export function useMfa() {
  const queryClient = useQueryClient();
  const setupInit = useMutation({
    mutationFn: () => request('/setup/initiate', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
  });
  const setupVerify = useMutation({
    mutationFn: (code: string) => request('/setup/verify', { method: 'POST', body: { code } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
  });
  const verify = useMutation({
    mutationFn: (body: { user_id: string; code: string }) =>
      request('/verify', { method: 'POST', body }),
  });
  const disable = useMutation({
    mutationFn: (body: { password: string; code: string }) =>
      request('/disable', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
  });
  const regenerate = useMutation({
    mutationFn: (code: string) =>
      request('/backup-codes/regenerate', { method: 'POST', body: { code } }),
  });
  return {
    setupMfa: () => setupInit.mutateAsync().then((r) => ({
      secret: r.secret,
      qrCode: r.qr_code,
      backupCodes: r.backup_codes || [],
    })),
    verifySetup: (code: string) => setupVerify.mutateAsync(code).then(() => true),
    verifyCode: (userId: string, code: string) =>
      verify.mutateAsync({ user_id: userId, code }).then((r) => r.valid === true),
    disableMfa: (password: string, code: string) =>
      disable.mutateAsync({ password, code }).then(() => true),
    regenerateBackupCodes: (code: string) =>
      regenerate.mutateAsync(code).then((r) => r.backup_codes || []),
    isSettingUp: setupInit.isPending || setupVerify.isPending,
    isVerifying: verify.isPending,
  };
}
